import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Xmark, Mail } from "iconoir-react";
import { verifyMagicLink, requestMagicLink, saveActiveHousehold, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function MagicVerify() {
    const nav = useNavigate();
    const [params] = useSearchParams();
    const { setUser } = useAuth();
    const token = params.get("token") || "";
    const [state, setState] = useState("loading"); // loading | error | resent
    const [errMsg, setErrMsg] = useState("");
    const [errEmail, setErrEmail] = useState("");
    const [resendBusy, setResendBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!token) {
                setState("error");
                setErrMsg("Missing link token.");
                return;
            }
            try {
                const data = await verifyMagicLink(token);
                if (cancelled) return;
                if (data.session_token) localStorage.setItem("milo_session_token", data.session_token);
                setUser(data.user);
                if (data.household && data.member) {
                    saveActiveHousehold({
                        household_id: data.household.id,
                        household_name: data.household.name,
                        household_code: data.household.code,
                        member_id: data.member.id,
                        member_name: data.member.name,
                    });
                }
                await routeToNext(data);
            } catch (err) {
                if (cancelled) return;
                setErrMsg(err?.response?.data?.detail || "This link is invalid or expired.");
                // Best-effort: pick up the email from the failed request so we can offer a one-tap resend
                const emailFromErr = err?.response?.data?.email;
                if (emailFromErr) setErrEmail(emailFromErr);
                setState("error");
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const routeToNext = async (data) => {
        // Compute the destination, but use a HARD navigation so the AuthProvider
        // re-mounts with the just-stored session token (avoids a state-propagation
        // race where <Protected> still sees user=null and bounces to /login).
        let dest = "/picker";
        if (data?.household) {
            dest = "/app";
        } else {
            try {
                const { data: list } = await api.get("/me/households");
                if (list?.length === 1) {
                    const e0 = list[0];
                    saveActiveHousehold({
                        household_id: e0.household.id,
                        household_name: e0.household.name,
                        household_code: e0.household.code,
                        member_id: e0.member.id,
                        member_name: e0.member.name,
                    });
                    dest = "/app";
                } else if (list?.length > 1) {
                    dest = "/picker";
                }
            } catch (e) { console.warn("pages/MagicVerify.jsx:", e); }
        }
        window.location.replace(dest);
    };

    const resendLink = async () => {
        const e = (errEmail || "").trim();
        if (!e || !/.+@.+\..+/.test(e)) {
            toast.error("Enter a valid email");
            return;
        }
        setResendBusy(true);
        try {
            const inviteCode = sessionStorage.getItem("milo_pending_code") || undefined;
            await requestMagicLink({ email: e, invite_code: inviteCode });
            setState("resent");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't send link. Try again in a moment.");
        } finally {
            setResendBusy(false);
        }
    };

    return (
        <div className="auth-bg min-h-screen w-full flex items-center justify-center p-5">
            <div className="auth-sun" />
            <div className="auth-heart" />
            <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative z-10 w-full max-w-md"
            >
                <div className="flex flex-col items-center text-center mb-6">
                    <img src="/milo-logo.png" alt="Milo" className="h-14 w-auto object-contain mb-6" />
                </div>

                <div className="bg-white/80 backdrop-blur-xl rounded-[28px] p-6 sm:p-7 shadow-[0_2px_4px_rgba(13,42,27,0.04),0_24px_48px_rgba(13,42,27,0.08)] border border-white/40">
                    {state === "loading" && (
                        <div className="text-center py-6" data-testid="magic-loading">
                            <div className="font-display text-3xl font-bold tracking-tight">Signing you in…</div>
                            <div className="text-sm text-[#5b6d63] mt-3">Hold tight, almost there.</div>
                        </div>
                    )}

                    {state === "error" && (
                        <div className="py-1" data-testid="magic-error">
                            <div className="text-center mb-5">
                                <div className="w-12 h-12 rounded-full bg-[#fbe0dc] flex items-center justify-center mx-auto mb-3">
                                    <Xmark className="w-6 h-6 text-[#A53F3F]" />
                                </div>
                                <div className="font-display text-3xl font-bold tracking-tight">Link didn't work</div>
                                <div className="text-sm text-[#5b6d63] mt-2">{errMsg}</div>
                            </div>

                            <div className="space-y-3">
                                <div>
                                    <div className="mb-2 flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full bg-[#e8f1e4] flex items-center justify-center">
                                            <Mail className="w-3.5 h-3.5 text-[#2F6B43]" />
                                        </div>
                                        <span className="text-sm font-semibold text-[#0d2a1b]">Send a fresh link to:</span>
                                    </div>
                                    <input
                                        data-testid="resend-email-input"
                                        type="email"
                                        className="brut-input"
                                        placeholder="you@family.com"
                                        value={errEmail}
                                        onChange={(e) => setErrEmail(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                                <button
                                    onClick={resendLink}
                                    disabled={resendBusy}
                                    data-testid="resend-link-btn"
                                    className="w-full flex items-center justify-center gap-2 bg-[#2F6B43] text-white rounded-2xl py-3.5 font-semibold text-base hover:bg-[#255636] transition-colors shadow-[0_6px_20px_rgba(47,107,67,0.3)] disabled:opacity-70"
                                >
                                    {resendBusy ? "Sending…" : "Email me a fresh link"}
                                </button>
                                <button
                                    onClick={() => nav("/login")}
                                    data-testid="magic-error-back-btn"
                                    className="w-full text-center text-sm font-semibold text-[#5b6d63] hover:text-[#2F6B43] py-1"
                                >
                                    ← Back to sign in
                                </button>
                            </div>
                        </div>
                    )}

                    {state === "resent" && (
                        <div className="text-center py-2" data-testid="magic-resent">
                            <div className="w-12 h-12 rounded-full bg-[#e8f1e4] flex items-center justify-center mx-auto mb-3">
                                <Mail className="w-6 h-6 text-[#2F6B43]" />
                            </div>
                            <div className="font-display text-3xl font-bold tracking-tight">Check your inbox</div>
                            <div className="text-sm text-[#255636] mt-3">
                                A fresh link is on its way to <b>{errEmail}</b>. Tap it and you're in.
                            </div>
                            <button
                                onClick={() => nav("/login")}
                                className="mt-5 text-sm font-semibold text-[#5b6d63] hover:text-[#2F6B43]"
                            >
                                Back to sign in
                            </button>
                        </div>
                    )}
                </div>
            </motion.div>
        </div>
    );
}
